import * as Sentry from "@sentry/react";
import { lazy, StrictMode, useEffect } from "react";
import { createRoot } from "react-dom/client";
import {
  BrowserRouter,
  useLocation,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router";
import "./assets/fonts/Roobert/roobert.css";
import "./assets/fonts/nib/nib.css";
import MatomoTracker from "./lib/MatomoTracker.jsx";
import "./styles/globals.css";
import "./styles/reset.css";
import DemoBanner from "./features/demo/DemoBanner";

if (process.env.NODE_ENV === "production" && import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: process.env.NODE_ENV,
    initialScope: {
      tags: { mode: import.meta.env.VITE_MODE },
    },
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.browserProfilingIntegration(),
    ],
    tracesSampleRate: 0.1,
    tracePropagationTargets: [
      "localhost",
      window.location.host,
      import.meta.env.VITE_API_HOST,
    ].filter(Boolean),
    profileSessionSampleRate: 0.1,
    profileLifecycle: "trace",
    beforeSend(event) {
      // Remove cookies from the request object if they exist
      if (event.request && event.request.headers) {
        delete event.request.headers["Cookie"];
      }

      if (event.request && event.request.cookies) {
        delete event.request.cookies;
      }

      return event;
    },
  });
}

let App;

if (import.meta.env.VITE_MODE === "show") {
  App = lazy(() => import("./show/ShowApp.jsx"));
} else {
  App = lazy(() => import("./App.jsx"));
}

let StagingBanner;
let MaintenanceBanner;

if (process.env.NODE_ENV.startsWith("staging")) {
  StagingBanner = lazy(() => import("./features/staging/StagingBanner.jsx"));
}

if (import.meta.env.VITE_MAINTENANCE_DATE) {
  MaintenanceBanner = lazy(
    () => import("./features/maintenance/MaintenanceBanner.jsx"),
  );
}

const container = document.getElementById("root");
const root = createRoot(container);

const MainApp = () => {
  const location = useLocation();
  const showDemoBanner = location.pathname.startsWith("/demo/");

  return (
    <>
      {!showDemoBanner && StagingBanner && <StagingBanner />}
      {!showDemoBanner && MaintenanceBanner && <MaintenanceBanner />}
      {showDemoBanner && <DemoBanner />}
      <App />
      {process.env.NODE_ENV === "production" && (
        <MatomoTracker
          siteId={import.meta.env.VITE_MATOMO_SITE_ID}
          matomoUrl={import.meta.env.VITE_MATOMO_URL}
          matomoScriptSrc={import.meta.env.VITE_MATOMO_SCRIPT_SRC}
        />
      )}
    </>
  );
};

root.render(
  <StrictMode>
    <BrowserRouter>
      <MainApp />
    </BrowserRouter>
  </StrictMode>,
);
